import { Award, Crown, Gem, Medal } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { PriceDisplay } from "@/components/ui/price-display";
import { cn } from "@/lib/utils";

import type {
  CustomerSegment,
  SegmentBreakdownEntryShape,
} from "../../types/discount-analytics-types";

interface Props {
  rows: SegmentBreakdownEntryShape[];
}

const SEGMENT_ORDER: CustomerSegment[] = ["PLATINUM", "GOLD", "SILVER", "BRONZE"];

const SEGMENT_THEME: Record<
  CustomerSegment,
  {
    icon: React.ComponentType<{ className?: string }>;
    bg: string;
    border: string;
    label: string;
    value: string;
    sub: string;
  }
> = {
  PLATINUM: {
    icon: Gem,
    bg: "bg-violet-100",
    border: "border-violet-300",
    label: "text-violet-900",
    value: "text-violet-700",
    sub: "text-violet-900",
  },
  GOLD: {
    icon: Crown,
    bg: "bg-amber-100",
    border: "border-amber-300",
    label: "text-amber-900",
    value: "text-amber-700",
    sub: "text-amber-900",
  },
  SILVER: {
    icon: Medal,
    bg: "bg-slate-100",
    border: "border-slate-300",
    label: "text-slate-900",
    value: "text-slate-700",
    sub: "text-slate-900",
  },
  BRONZE: {
    icon: Award,
    bg: "bg-orange-100",
    border: "border-orange-300",
    label: "text-orange-900",
    value: "text-orange-700",
    sub: "text-orange-900",
  },
};

export function SegmentBreakdownCard({ rows }: Props) {
  const { t } = useTranslation("reports");

  const indexed = new Map(rows.map((r) => [r.segment, r] as const));
  const totalRedemptions = rows.reduce((s, r) => s + r.redemptionCount, 0);

  if (totalRedemptions === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t("discounts.segments.title")}</CardTitle>
          <CardDescription>{t("discounts.segments.description")}</CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-sm font-medium italic text-slate-700">
            {t("discounts.segments.emptyState")}
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">{t("discounts.segments.title")}</CardTitle>
        <CardDescription>{t("discounts.segments.description")}</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {SEGMENT_ORDER.map((segment) => {
            const row = indexed.get(segment) ?? {
              segment,
              redemptionCount: 0,
              uniqueCustomers: 0,
              totalAmountDiscounted: 0,
              averageInWindowSpend: 0,
            };
            const theme = SEGMENT_THEME[segment];
            const Icon = theme.icon;
            const shareOfRedemptions =
              totalRedemptions > 0 ? row.redemptionCount / totalRedemptions : 0;

            return (
              <div
                key={segment}
                className={cn(
                  "rounded-lg border-2 p-4 space-y-3 shadow-sm",
                  theme.bg,
                  theme.border
                )}
              >
                <div className="flex items-center gap-2">
                  <Icon className={cn("h-4 w-4", theme.label)} />
                  <span className={cn("text-sm font-bold", theme.label)}>
                    {t(`discounts.segments.labels.${segment}`)}
                  </span>
                </div>

                <div>
                  <div className={cn("text-2xl font-bold tabular-nums", theme.value)}>
                    {row.uniqueCustomers}
                  </div>
                  <div className={cn("text-xs font-semibold", theme.sub)}>
                    {t("discounts.segments.uniqueCustomers")}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div>
                    <div className={cn("font-semibold", theme.sub)}>
                      {t("discounts.segments.redemptions")}
                    </div>
                    <div className={cn("mt-0.5 font-bold tabular-nums", theme.sub)}>
                      {row.redemptionCount}{" "}
                      <span className={cn("text-[10px] font-medium", theme.sub)}>
                        ({(shareOfRedemptions * 100).toFixed(0)}%)
                      </span>
                    </div>
                  </div>
                  <div>
                    <div className={cn("font-semibold", theme.sub)}>
                      {t("discounts.segments.amountDiscounted")}
                    </div>
                    <div className={cn("mt-0.5 font-bold", theme.sub)}>
                      <PriceDisplay amount={row.totalAmountDiscounted} />
                    </div>
                  </div>
                </div>

                <div className="text-xs">
                  <div className={cn("font-semibold", theme.sub)}>
                    {t("discounts.segments.averageInWindowSpend")}
                  </div>
                  <div className={cn("mt-0.5 font-bold", theme.sub)}>
                    <PriceDisplay amount={Math.round(row.averageInWindowSpend)} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
